import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import './styles/Report.css';
import { UserContext } from '../UserContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import moment from 'moment';
import { FaDownload } from "react-icons/fa";
import { FaDeleteLeft } from "react-icons/fa6";
import TableToExcel from "@linways/table-to-excel";
import { useNavigate } from 'react-router-dom';
import { VscSettings } from "react-icons/vsc";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Tabs from '../components/Tabs/Tabs';


const formatDate = (date) => {
  if (!date) return 'NA';
  const temp = date.split('T')[0];
  return moment(temp).format('DD-MM-YYYY');
};

const getVehicleDetails = (req) => {
  if (req.travelPreference === 'Train') {
    return {
      vehicleName: req.trainNumber || 'NA',
      vehicleTiming: req.trainTiming || 'NA',
    };
  }

  if (req.travelPreference === 'Bus') {
    return {
      vehicleName: req.busName || 'NA',
      vehicleTiming: req.busTiming || 'NA',
    };
  }

  return {
    vehicleName: 'NA',
    vehicleTiming: 'NA',
  };
};

const getPassengerAgeLabel = (passenger) => {
  if (passenger?.age) return passenger.age;
  if (passenger?.ageDisplay) return passenger.ageDisplay;
  if (!passenger?.dob) return 'NA';

  const dob = new Date(passenger.dob);
  if (Number.isNaN(dob.getTime())) return 'NA';

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? String(age) : 'NA';
};

const getPassengerProofValue = (passenger) =>
  passenger?.idProof || passenger?.existingIdProof || passenger?.idProofUrl || '';

const getPassengerContextValue = (passenger, request, key) => {
  const passengerValue = passenger?.[key];
  if (passengerValue !== null && passengerValue !== undefined && passengerValue !== '') {
    return passengerValue;
  }

  const requestValue = request?.[key];
  if (requestValue !== null && requestValue !== undefined && requestValue !== '') {
    return requestValue;
  }

  return 'NA';
};

const FilePreviewModal = ({ data, onClose }) => {
 
  useEffect(() => {
    if (!data) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [data, onClose]);

  // Early return AFTER all hooks
  if (!data) return null;

  const { type, url } = data;
  const cleanName = url.split("?")[0].split("$")[0].split("/").pop();
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  const downloadDocument = (url) => {
    if (!url) return;
    const cleanUrl = url.split("?")[0].split("$")[0];
    const fileName = cleanUrl.split("/").pop();
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="doc-overlay" onClick={handleOverlayClick}>
      <div className="doc-card" onClick={(e) => e.stopPropagation()}>
        <button 
          className="doc-close-btn" 
          onClick={handleCloseClick}
          aria-label="Close modal"
        >
          ✕
        </button>

        {type === "preview" ? (
          <iframe src={url} className="doc-iframe" title="preview" />
        ) : (
          <div className="no-preview-box">
            <h3>No Preview Available</h3>
            <p>This document cannot be displayed. Please download it to view.</p>
            <button 
              className="download-btn" 
              onClick={() => downloadDocument(url)}
            >
              Download Document
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Report = () => {
  const tableRef = useRef(null);
  const filterRef = useRef(null);

  const navigate = useNavigate();
  const { user, token } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]); 

  const [filterOptions, setFilterOptions] = useState({
    requests: ['All'],
    requesterType:['All'],
    supportType: ['All'],
    status: ['All'],
    purpose: ['All'],
    costCenter: ['All'],
    requestStatus: ['All'],
    bookingStatus: ['All'],
  });
  const [dropdownVisible, setDropdownVisible] = useState({
    requests: false,
    requesterType:false,
    supportType: false,
    status: false,
    purpose: false,
    costCenter: false,
    requestStatus: false,
    bookingStatus: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchHeader, setSearchHeader] = useState('requesterName');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setDropdownVisible({
          requests: false,
          requesterType:false,
          supportType: false,
          status: false,
          purpose: false,
          costCenter: false,
          requestStatus: false,
          bookingStatus: false,
        });
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/requests/all`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (!res.ok) {
          if (res.status === 401) navigate('/login');
          throw new Error('Failed fetching requests');
        }

        const reqs = await res.json();
        if (cancelled) return;

        // Process the data
        const processed = reqs.map(req => {
          const { vehicleName, vehicleTiming } = getVehicleDetails(req);

          // Travel & Accommodation Logic
          if (!req.supportNeeded || !req.supportNeeded.includes('Courier')) {
            return {
              id: req.id,
              userId: req.userId,
              requesterName: req.user?.name || "NA",
              requesterType: req.user?.requesterType || "NA",
              supportNeeded: req.supportNeeded,
              location: req.supportNeeded === 'Accommodation' ? req.destination : `${req.source} to ${req.destination}`,
              date: req.supportNeeded === 'Travel' ? formatDate(req.fromDate) : `${formatDate(req.fromDate)} to ${formatDate(req.toDate)}`,
              totalCount: req.individuals?.length || 0,
              status: req.status,
              bookingStatus: req.bookingDetails?.bookingStatus || 'NA',
              bookingType: req.bookingDetails?.selfBooked ? 'Self Booking' : 'Online',
              reimbursementStatus: req.bookingDetails?.reimbursementStatus || 'NA',
              requestDate: formatDate(req.createdAt),
              batchId: req.batchId,
              amount: req.bookingDetails?.amount ?? 'NA',
              bookingBy: req.bookingDetails?.bookingBy ?? 'NA',
              bookingDate: req.bookingDetails?.bookingDate ? formatDate(req.bookingDetails.bookingDate) : 'NA',
              pnrNumber: req.bookingDetails?.pnrNumber ?? 'NA',
              passengersName: req.individuals || [],
              center: req.center,
              client: req.client,
              purpose: req.purpose || req.otherPurpose || 'NA',
              travelPreference: req.travelPreference || 'NA',
              travelClass: req.travelClass || 'NA',
              vehicleName,
              vehicleTiming,
              tripType: req.tripType || 'One Way',
              returnSource: req.returnSource || 'NA',
              returnDestination: req.returnDestination || 'NA',
              returnDate: req.returnDate
                ? formatDate(req.returnDate)
                : 'NA',
              costCenter: req.costCenter,
              bookingDetails: req.bookingDetails?.fileUrl ? req.bookingDetails.fileUrl : '',
              
              adminRemarks: req.adminRemarks || [],
              cancelReason: req.bookingDetails?.cancelReason ? req.bookingDetails.cancelReason : 'NA',
              cancelProof: req.bookingDetails?.cancelProof ? req.bookingDetails.cancelProof.split('$')[1] : '',
              refundAmount: req.bookingDetails?.refundAmount ? req.bookingDetails.refundAmount: 'NA',
              lostAmount: req.bookingDetails?.lostAmount ? req.bookingDetails.lostAmount : 'NA',
              selfBookedTicketAmount: req.bookingDetails?.selfBookedTicketAmount || 'NA',
              selfBookedTicketDetails: req.bookingDetails?.selfBookedTicketDetails || 'NA',
              selfBookedTicketBill: req.bookingDetails?.selfBookedTicketBill ? req.bookingDetails.selfBookedTicketBill.split('$')[1] : '',
              reimbursementApprovedBy: req.bookingDetails?.reimbursementApprovedBy || 'NA',
              reimbursementApprovedDate: req.bookingDetails?.reimbursementApprovedDate
                ? formatDate(req.bookingDetails.reimbursementApprovedDate)
                : 'NA',
              reimbursementRejectedBy: req.bookingDetails?.reimbursementRejectedBy || 'NA',
              reimbursementRejectedDate: req.bookingDetails?.reimbursementRejectedDate
                ? formatDate(req.bookingDetails.reimbursementRejectedDate)
                : 'NA',
              paymentProof: req.bookingDetails?.paymentProof ? req.bookingDetails.paymentProof.split('$')[1] : '',
              rejectionRemarks:
                req.bookingDetails?.reimbursementStatus === 'Rejected' && req.adminRemarks?.length
                  ? req.adminRemarks.join(', ')
                  : 'NA',
            };
          } 
          // Courier Logic
          else {
            return {
              id: req.id,
              userId: req.userId,
              requesterName: req.user?.name || "NA",
              requesterType: req.user?.requesterType || "NA",
              requestDate: formatDate(req.createdAt),
              supportNeeded: req.supportNeeded,
              courierType: req.courierType,
              officeLocation: req.officeLocation,
              fromLocation: req.fromLocation ?? 'NA',
              toLocation: req.toLocation ?? 'NA',
              sentFrom: req.sentFrom ?? 'NA',
              receivedBy: req.receivedBy ?? 'NA',
              receivedStatus: req.receivedStatus ?? 'No',
              receivedOn: req.receivedOn ? formatDate(req.receivedOn) : 'NA',
              status: req.status,
              bookingStatus: req.bookingDetails?.bookingStatus || 'NA',
              bookingType: 'Online',
              reimbursementStatus: 'NA',
              amount: req.bookingDetails?.amount ?? 'NA',
              bookingBy: req.bookingDetails?.bookingBy ?? 'NA',
              bookingDate: req.bookingDetails?.bookingDate ? formatDate(req.bookingDetails.bookingDate) : 'NA',
              podNumber: req.bookingDetails?.podNumber ?? 'NA',
              courierCo: req.bookingDetails?.courierCo ?? 'NA',
              bookingDetails: req.bookingDetails?.fileUrl ? req.bookingDetails.fileUrl : '',
              adminRemarks: req.adminRemarks || [],
              cancelReason: req.bookingDetails?.cancelReason ? req.bookingDetails.cancelReason : 'NA',
              cancelProof: req.bookingDetails?.cancelProof ? req.bookingDetails.cancelProof.split('$')[1] : '',
              refundAmount: req.bookingDetails?.refundAmount ?? 'NA',
              lostAmount: req.bookingDetails?.lostAmount ?? 'NA',
              selfBookedTicketAmount: 'NA',
              selfBookedTicketBill: '',
              reimbursementApprovedBy: 'NA',
              reimbursementApprovedDate: 'NA',
              reimbursementRejectedBy: 'NA',
              reimbursementRejectedDate: 'NA',
              rejectionRemarks: 'NA',
            };
          }
        });

        if (!cancelled) setData(processed);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (user && user.role !== 'USER') fetchAll(); 
    else setLoading(false);

    return () => { cancelled = true; };
  }, [user, token, navigate]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let filtered = data;
    const currentDate = moment().startOf('day');

    // search
    if (searchTerm && !['requestDate', 'bookingDate', 'date', 'receivedOn', 'reimbursementApprovedDate', 'reimbursementRejectedDate'].includes(searchHeader)) {
      filtered = filtered.filter((req) => {
        if (searchHeader === 'passengerName') {
          return req.passengersName?.some((p) =>
            p.name?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        } else if (searchHeader === 'requestId') {
          return String(req.id)
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }

        const val = req[searchHeader];
        return (
          typeof val === 'string' &&
          val.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    // date-range search with FIXED split errors
    if (['requestDate', 'bookingDate', 'date', 'receivedOn', 'reimbursementApprovedDate', 'reimbursementRejectedDate'].includes(searchHeader) && dateRange.start && dateRange.end) {
      const start = moment(dateRange.start).startOf('day');
      const end = moment(dateRange.end).endOf('day');

      filtered = filtered.filter((req) => {
        let check;

        switch (searchHeader) {
          case 'requestDate': 
            check = moment(req.requestDate, 'DD-MM-YYYY');
            break;
          case 'bookingDate':
            check = moment(req.bookingDate, 'DD-MM-YYYY');
            break;
          case 'receivedOn':
            check = moment(req.receivedOn, 'DD-MM-YYYY');
            break;
          case 'reimbursementApprovedDate':
            check = moment(req.reimbursementApprovedDate, 'DD-MM-YYYY');
            break;
          case 'reimbursementRejectedDate':
            check = moment(req.reimbursementRejectedDate, 'DD-MM-YYYY');
            break;
          case 'date': {
            const raw = req.date || '';

            if (req.supportNeeded === 'Travel') {
              const d = moment(raw ? raw.split('-').reverse().join('-') : '', 'YYYY-MM-DD');
              return d.isBetween(start, end, null, '[]');
            }

            // travel + accommodation
            const [from, to] = raw.includes(' to ') ? raw.split(' to ') : ['', ''];

            const f = moment(from ? from.split('-').reverse().join('-') : '', 'YYYY-MM-DD');
            const t = moment(to ? to.split('-').reverse().join('-') : '', 'YYYY-MM-DD');

            if (!f.isValid() && !t.isValid()) return false;

            return (
              f.isBetween(start, end, null, '[]') || 
              t.isBetween(start, end, null, '[]') || 
              (f.isBefore(end) && t.isAfter(start))
            );
          }
          default:
            check = null;
        }

        return check ? check.isBetween(start, end, null, '[]') : false;
      });
    }

    // requests filter
    if (!filterOptions.requests.includes('All')) {
      filtered = filtered.filter(req => filterOptions.requests.includes(req.userId === user.id ? 'My Requests' : 'All'));
    }

    // requester type
    if (!filterOptions.requesterType.includes('All')) {
      filtered = filtered.filter(req => filterOptions.requesterType.includes(req.requesterType));
    }

    // support type
    if (!filterOptions.supportType.includes('All')) {
      filtered = filtered.filter(req => filterOptions.supportType.includes(req.supportNeeded));
    }

    // status open/closed — FIXED SPLIT CRASH HERE
    if (!filterOptions.status.includes('All')) {
      filtered = filtered.filter((req) => {
        const raw = req.date || '';

        if (filterOptions.status.includes('Open')) {
          if (req.supportNeeded === 'Travel') {
            const d = moment(
              raw ? raw.split('-').reverse().join('-') : '',
              'YYYY-MM-DD'
            );
            return d.isSameOrAfter(currentDate);
          } else {
            const [_, to] = raw.includes(' to ') ? raw.split(' to ') : ['', ''];
            const t = moment(to ? to.split('-').reverse().join('-') : '', 'YYYY-MM-DD');
            return t.isSameOrAfter(currentDate);
          }
        }

        if (filterOptions.status.includes('Closed')) {
          if (req.supportNeeded === 'Travel') {
            const d = moment(
              raw ? raw.split('-').reverse().join('-') : '',
              'YYYY-MM-DD'
            );
            return d.isBefore(currentDate);
          } else {
            const [_, to] = raw.includes(' to ') ? raw.split(' to ') : ['', ''];
            const t = moment(to ? to.split('-').reverse().join('-') : '', 'YYYY-MM-DD');
            return t.isBefore(currentDate);
          }
        }

        return false;
      });
    }

    // purpose
    if (!filterOptions.purpose.includes('All')) {
      let stored = [...filtered];
      filtered = filtered.filter(req => req.passengersName?.some(p => filterOptions.purpose.includes(p.purpose)));
      if (filterOptions.purpose.includes('Others')) {
        stored = stored.filter(req => req.passengersName?.some(p => !['Assessment','Meeting'].includes(p.purpose)));
        const map = new Map();
        [...filtered, ...stored].forEach(r => map.set(r.id, r));
        filtered = Array.from(map.values());
      }
    }

    // cost center
    if (!filterOptions.costCenter.includes('All')) {
      let stored = [...filtered];
      filtered = filtered.filter(req => filterOptions.costCenter.includes(req.costCenter));
      if (filterOptions.costCenter.includes('Others')) {
        stored = stored.filter(req => filterOptions.costCenter.includes(req.costCenter));
        const map = new Map();
        [...filtered, ...stored].forEach(r => map.set(r.id, r));
        filtered = Array.from(map.values());
      }
    }

    // requestStatus & bookingStatus
    if (!filterOptions.requestStatus.includes('All')) {
      filtered = filtered.filter(req => filterOptions.requestStatus.includes(req.status));
    }
    if (!filterOptions.bookingStatus.includes('All')) {
      filtered = filtered.filter(req => filterOptions.bookingStatus.includes(req.bookingStatus));
    }

    return filtered;
  }, [data, filterOptions, searchTerm, searchHeader, dateRange, user]);

  // compute serial maps for stable S.No
  const { travelSerialMap, courierSerialMap } = useMemo(() => {
    const tMap = new Map();
    const cMap = new Map();
    let t = 1, c = 1;
    for (const r of filteredData) {
      if (r.supportNeeded && r.supportNeeded.includes('Courier')) cMap.set(r.id, c++);
      else tMap.set(r.id, t++);
    }
    return { travelSerialMap: tMap, courierSerialMap: cMap };
  }, [filteredData]);

  // handlers
  const handleFilterChange = (filterCategory, value) => {
    setFilterOptions(prev => {
      const next = { ...prev };
      if (next[filterCategory].includes(value)) {
        next[filterCategory] = next[filterCategory].filter(i => i !== value);
      } else {
        next[filterCategory].push(value);
      }
      return next;
    });
  };

  const toggleDropdown = (category) => {
    setDropdownVisible(prev => {
      const next = { ...prev, [category]: !prev[category] };
      Object.keys(next).forEach(k => { if (k !== category) next[k] = false; });
      return next;
    });
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSearchHeaderChange = (e) => setSearchHeader(e.target.value);
  const handleFilterButtonClick = () => setShowFilters(s => !s);
  const handleClearFilters = () => {
    setFilterOptions({ 
      requests: ['All'], 
      requesterType:['All'], 
      supportType: ['All'], 
      status: ['All'], 
      purpose: ['All'], 
      costCenter: ['All'], 
      requestStatus: ['All'], 
      bookingStatus: ['All'] 
    });
    setSearchTerm(''); 
    setSearchHeader('requesterName'); 
    setDropdownVisible({ 
      requests: false, 
      requesterType:false, 
      supportType: false, 
      status: false, 
      purpose: false, 
      costCenter: false, 
      requestStatus: false, 
      bookingStatus: false 
    });
    setDateRange({ start: null, end: null });
  };

  const handleDownload = (title) => {
    TableToExcel.convert(tableRef.current, { 
      name: `${title}_Desk_${new Date().toLocaleDateString()}.xlsx`, 
      sheet: { name: 'Report' } 
    });
  };

  const tabData = [{ label: 'Travel' }, { label: 'Courier' }];

  // FIXED FILE PREVIEW HANDLER
  const handleFilePreview = async (url) => {
    if (!url) return;

    try {
      setIsPreviewLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/requests/file-access?fileUrl=${encodeURIComponent(url)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate secure file URL');
      }

      const { url: signedUrl } = await response.json();
      const clean = url.split("?")[0].split("$")[0].toLowerCase();
      const extension = clean.substring(clean.lastIndexOf("."));
      const unsupported = [".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt"];

      if (unsupported.includes(extension)) {
        setPreviewUrl({ type: "unsupported", url: signedUrl });
      } else {
        setPreviewUrl({ type: "preview", url: signedUrl });
      }
    } catch (error) {
      console.error('Secure file preview failed:', error);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Render UI
  return (
    <div className='Report'>
      <Header />
      <Tabs tabs={tabData}>
        {/* TRAVEL TAB */}
        <div className="report-page">
          <div className='report-header'>
            <h2>Summary</h2>
            <div className='search-filter' ref={filterRef}>
              {showFilters && (
                <div className={`filter-options ${showFilters ? 'open-menu' : ''}`}>
                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.requests ? 'open-menu' : ''}`}>
                      {['All', 'My Requests'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.requests.includes(option)} 
                            onChange={() => handleFilterChange('requests', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('requests')}>Requests</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.requesterType ? 'open-menu' : ''}`}>
                      {['All', 'Assessor', 'Proctor', 'SME', 'Employee', 'Employee_Assessor','Trainer', 'Counselor', 'Intern'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.requesterType.includes(option)} 
                            onChange={() => handleFilterChange('requesterType', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('requesterType')}>Requester Type</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.supportType ? 'open-menu' : ''}`}>
                      {['All', 'Travel', 'Accommodation', 'Travel + Accommodation'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.supportType.includes(option)} 
                            onChange={() => handleFilterChange('supportType', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('supportType')}>Support Type</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.status ? 'open-menu' : ''}`}>
                      {['All', 'Open', 'Closed'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.status.includes(option)} 
                            onChange={() => handleFilterChange('status', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('status')}>Open/Closed</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.purpose ? 'open-menu' : ''}`}>
                      {['All', 'Assessment', 'Meeting', 'Others'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.purpose.includes(option)} 
                            onChange={() => handleFilterChange('purpose', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('purpose')}>Purpose</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.costCenter ? 'open-menu' : ''}`}>
                      {['All', 'Govt-Central', 'Govt-State', 'Govt-Others', 'Others'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.costCenter.includes(option)} 
                            onChange={() => handleFilterChange('costCenter', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('costCenter')}>Cost Center</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.requestStatus ? 'open-menu' : ''}`}>
                      {['All', 'Pending', 'Accepted', 'Denied', 'Cancelled'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.requestStatus.includes(option)} 
                            onChange={() => handleFilterChange('requestStatus', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('requestStatus')}>Request Status</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.bookingStatus ? 'open-menu' : ''}`}>
                      {['All', 'Success', 'Fail', 'Pending', 'Cancelled'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.bookingStatus.includes(option)} 
                            onChange={() => handleFilterChange('bookingStatus', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('bookingStatus')}>Booking Status</button>
                  </div>
                </div>
              )}

              <div className="search-container">
                <div className="search-fields">
                  <label>
                    <strong>Search By:</strong>
                    <select value={searchHeader} onChange={handleSearchHeaderChange}>
                      <option value="requesterName">Requester Name</option>
                      <option value="requestId">Request ID</option>
                      <option value="requestDate">Request Date</option>
                      <option value="batchId">Batch ID</option>
                      <option value="center">Center</option>
                      <option value="client">Client</option>
                      <option value="location">Location</option>
                      <option value="date">Date of Journey</option>
                      <option value="passengerName">Passenger Name</option>
                      <option value="bookingBy">Booking By</option>
                      <option value="bookingDate">Booking Date</option>
                      <option value="amount">Amount</option>
                      <option value="pnrNumber">PNR Number</option>
                      <option value="remarks">Remarks</option>
                      <option value="cancelReason">Cancel Reason</option>
                      <option value="refundAmount">Refund Amount</option>
                      <option value="lostAmount">Lost Amount</option>
                      <option value="bookingType">Booking Type</option>
                      <option value="selfBookedTicketAmount">Self Ticket Amount</option>
                      <option value="reimbursementStatus">Reimbursement Status</option>
                      <option value="reimbursementApprovedBy">Reimbursement Approved By</option>
                      <option value="reimbursementApprovedDate">Reimbursement Approved Date</option>
                      <option value="reimbursementRejectedBy">Reimbursement Rejected By</option>
                      <option value="reimbursementRejectedDate">Reimbursement Rejected Date</option>
                    </select>
                  </label>

                  {!['requestDate', 'bookingDate', 'date', 'reimbursementApprovedDate', 'reimbursementRejectedDate'].includes(searchHeader) ? (
                    <div className='search-input'>
                      <strong>Search Here:</strong>
                      <input 
                        type="text" 
                        value={searchTerm} 
                        onChange={handleSearchChange} 
                        placeholder='Search here...' 
                      />
                    </div>
                  ) : (
                    <div className='search-input'>
                      <strong>Search Here:</strong>
                      <DatePicker 
                        selected={dateRange.start} 
                        onChange={(dates) => setDateRange({ start: dates[0], end: dates[1] })} 
                        startDate={dateRange.start} 
                        endDate={dateRange.end} 
                        selectsRange 
                        className='date-picker' 
                      />
                    </div>
                  )}
                </div>
              </div>

              <button className='filter-button' onClick={handleFilterButtonClick}>
                <VscSettings />
                <span><strong>Filter</strong></span>
              </button>
              <button className='filter-button' onClick={handleClearFilters}>
                <FaDeleteLeft />
                <span><strong>Clear</strong></span>
              </button>
            </div>

            <button className='download-button' onClick={() => handleDownload('Travel')}>
              <FaDownload />
              <span>Download</span>
            </button>
          </div>

          {isPreviewLoading && <div className='loading'>Opening file...</div>}
          {previewUrl && <FilePreviewModal data={previewUrl} onClose={() => setPreviewUrl(null)} />}

          <div className="report-table">
            <div className="table-scroll-x">
              <table ref={tableRef}>
                <thead>
                  <tr>
                    <th data-f-bold='true'>S.No</th>
                    <th data-f-bold='true'>Request Date</th>
                    <th data-f-bold='true'>Request ID</th>
                    <th data-f-bold='true'>Requester Name</th>
                    <th data-f-bold='true'>Requester Type</th>
                    <th data-f-bold='true'>Support Needed</th>
                    <th data-f-bold='true'>Batch Id</th>
                    <th data-f-bold='true'>Location</th>
                    <th data-f-bold='true'>Amount (In Rs)</th>
                    <th data-f-bold='true'>Booking By</th>
                    <th data-f-bold='true'>Booking Date</th>
                    <th data-f-bold='true'>Date of Journey</th>
                    <th data-f-bold='true'>PNR Number</th>
                    <th data-f-bold='true'>Passengers Count</th>
                    <th data-f-bold='true'>Passengers Name-Type</th>
                    <th data-f-bold="true">Gender</th>
                    <th data-f-bold="true">Age</th>
                    <th data-f-bold="true">ID Type</th>
                    <th data-f-bold="true">ID Number</th>
                    <th data-f-bold="true">Contact Number</th>
                    <th data-f-bold="true">ID Proof</th>
                    <th data-f-bold="true">Travel Preference</th>
                    <th data-f-bold="true">Travel Class</th>
                    <th data-f-bold="true">Vehicle Name</th>
                    <th data-f-bold="true">Departure Timing</th>
                    <th>Trip Type</th>
                    <th>Return Source</th>
                    <th>Return Destination</th>
                    <th>Return Journey Date</th>
                    <th data-f-bold='true'>Project</th>
                    <th data-f-bold='true'>Vertical</th>
                    <th data-f-bold='true'>Purpose</th>
                    <th data-f-bold='true'>Center</th>
                    <th data-f-bold='true'>Client</th>
                    <th data-f-bold='true'>Cost-Center</th>
                    <th data-f-bold='true'>Status</th>
                    <th data-f-bold='true'>Booking Type</th>
                    <th data-f-bold='true'>Ticket Amount</th>
                    <th data-f-bold='true'>Ticket Details</th>
                    <th data-f-bold='true'>Reimbursement Status</th>
                    <th data-f-bold='true'>Reimbursement Approved By</th>
                    <th data-f-bold='true'>Reimbursement Approved Date</th>
                    <th data-f-bold='true'>Reimbursement Rejected By</th>
                    <th data-f-bold='true'>Reimbursement Rejected Date</th>
                    <th data-f-bold='true'>Payment Proof</th>
                    <th data-f-bold='true'>Booking Status</th>
                    <th data-f-bold='true'>Booking Details</th>
                    <th data-f-bold='true'>Remarks</th>
                    <th data-f-bold='true'>Cancel Reason</th>
                    <th data-f-bold='true'>Cancel Proof</th>
                    <th data-f-bold='true'>Refund Amount (In Rs)</th>
                    <th data-f-bold='true'>Lost Amount (In Rs)</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr><td colSpan="52" className='loading'>Loading...</td></tr>
                  ) : (
                    filteredData && filteredData.map((request) => (
                      <React.Fragment key={request.id}>
                        {request.passengersName && request.passengersName.map((passenger, passengerIndex) => (
                          <React.Fragment key={`${request.id}-${passengerIndex}`}>
                            {passengerIndex === 0 && (
                              <tr>
                                <td rowSpan={request.passengersName.length}>{travelSerialMap.get(request.id)}</td>
                                <td rowSpan={request.passengersName.length}>{request.requestDate}</td>
                                <td rowSpan={request.passengersName.length}>{request.id}</td>
                                <td rowSpan={request.passengersName.length}>{request.requesterName}</td>
                                <td rowSpan={request.passengersName.length}>{request.requesterType}</td>
                                <td rowSpan={request.passengersName.length}>{request.supportNeeded}</td>
                                <td rowSpan={request.passengersName.length}>{request.batchId}</td>
                                <td rowSpan={request.passengersName.length}>{request.location}</td>
                                <td rowSpan={request.passengersName.length}>{request.amount}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingBy}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingDate}</td>
                                <td rowSpan={request.passengersName.length}>{request.date}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Online' ? request.pnrNumber : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.totalCount}</td>
                                <td>{passenger.name}-{passenger.type}</td>
                                <td>{passenger.gender || 'NA'}</td>
                                <td>{getPassengerAgeLabel(passenger)}</td>
                                <td>{passenger.idType || 'NA'}</td>
                                <td>{passenger.idNumber || 'NA'}</td>
                                <td>{passenger.contactNumber || 'NA'}</td>
                                <td data-hyperlink={getPassengerProofValue(passenger)} data-f-color={getPassengerProofValue(passenger) ? 'FF0000FF' : ''}>
                                  {getPassengerProofValue(passenger) ? (
                                    <span className="view-file-link" onClick={() => handleFilePreview(getPassengerProofValue(passenger))}>
                                      View file
                                    </span>
                                  ) : 'NA'}
                                </td>
                                <td rowSpan={request.passengersName.length}>{request.travelPreference}</td>
                                <td rowSpan={request.passengersName.length}>{request.travelClass}</td>
                                <td rowSpan={request.passengersName.length}>{request.vehicleName}</td>
                                <td rowSpan={request.passengersName.length}>{request.vehicleTiming}</td>
                                <td rowSpan={request.passengersName.length}>{request.tripType}</td>
                                <td rowSpan={request.passengersName.length}>{request.tripType === 'Round Trip' ? request.returnSource : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.tripType === 'Round Trip' ? request.returnDestination : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.tripType === 'Round Trip' ? request.returnDate : 'NA'}</td>
                                <td>{getPassengerContextValue(passenger, request, 'project')}</td>
                                <td>{getPassengerContextValue(passenger, request, 'vertical')}</td>
                                <td>{getPassengerContextValue(passenger, request, 'purpose')}</td>
                                <td rowSpan={request.passengersName.length}>{request.center}</td>
                                <td rowSpan={request.passengersName.length}>{request.client}</td>
                                <td rowSpan={request.passengersName.length}>{request.costCenter}</td>
                                <td rowSpan={request.passengersName.length}>{request.status}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Self Booking' ? request.selfBookedTicketAmount : 'NA'}</td>
                                <td rowSpan={request.passengersName.length} data-hyperlink={request.bookingType === 'Self Booking' ? request.selfBookedTicketBill : ''} data-f-color={request.bookingType === 'Self Booking' && request.selfBookedTicketBill ? 'FF0000FF' : ''}>
                                  {request.bookingType === 'Self Booking' && request.selfBookedTicketBill ? (
                                    <span className="view-file-link" onClick={() => handleFilePreview(request.selfBookedTicketBill)}>
                                      View file
                                    </span>
                                  ) : 'NA'}
                                </td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Self Booking' ? request.reimbursementStatus : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Self Booking' && request.reimbursementStatus === 'Approved' ? request.reimbursementApprovedBy : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Self Booking' && request.reimbursementStatus === 'Approved' ? request.reimbursementApprovedDate : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Self Booking' && request.reimbursementStatus === 'Rejected' ? request.reimbursementRejectedBy : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Self Booking' && request.reimbursementStatus === 'Rejected' ? request.reimbursementRejectedDate : 'NA'}</td>
                                <td rowSpan={request.passengersName.length} data-hyperlink={request.bookingType === 'Self Booking' ? request.paymentProof : ''} data-f-color={request.bookingType === 'Self Booking' && request.paymentProof ? 'FF0000FF' : ''}>
                                  {request.bookingType === 'Self Booking' && request.paymentProof ? (
                                    <span className="view-file-link" onClick={() => handleFilePreview(request.paymentProof)}>
                                      View file
                                    </span>
                                  ) : 'NA'}
                                </td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Online' ? request.bookingStatus : 'NA'}</td>
                                <td rowSpan={request.passengersName.length} data-hyperlink={request.bookingType === 'Online' ? request.bookingDetails : ''} data-f-color={request.bookingType === 'Online' && request.bookingDetails ? 'FF0000FF' : ''}>
                                  {request.bookingType === 'Online' && request.bookingDetails ? (
                                    <span className="view-file-link" onClick={() => handleFilePreview(request.bookingDetails)}>
                                      View file
                                    </span>
                                  ) : 'NA'}
                                </td>
                                <td rowSpan={request.passengersName.length}>
                                  {request.adminRemarks && request.adminRemarks.length > 0 
                                    ? request.adminRemarks.map((remark, idx) => (
                                        <div key={idx}>{idx + 1}) {remark}</div>
                                      )) 
                                    : 'NA'}
                                </td>
                                <td rowSpan={request.passengersName.length}>{request.cancelReason}</td>
                                <td rowSpan={request.passengersName.length} data-hyperlink={request.cancelProof} data-f-color={request.cancelProof ? 'FF0000FF' : ''}>
                                  {request.cancelProof ? (
                                    <span className="view-file-link" onClick={() => handleFilePreview(request.cancelProof)}>
                                      View file
                                    </span>
                                  ) : 'NA'}
                                </td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Online' ? request.refundAmount : 'NA'}</td>
                                <td rowSpan={request.passengersName.length}>{request.bookingType === 'Online' ? request.lostAmount : 'NA'}</td>
                              </tr>
                            )}
                            {passengerIndex !== 0 && (
                              <tr>
                                <td>{passenger.name}-{passenger.type}</td>
                                <td>{passenger.gender || 'NA'}</td>
                                <td>{getPassengerAgeLabel(passenger)}</td>
                                <td>{passenger.idType || 'NA'}</td>
                                <td>{passenger.idNumber || 'NA'}</td>
                                <td>{passenger.contactNumber || 'NA'}</td>
                                <td data-hyperlink={getPassengerProofValue(passenger)} data-f-color={getPassengerProofValue(passenger) ? 'FF0000FF' : ''}>
                                  {getPassengerProofValue(passenger) ? (
                                    <span className="view-file-link" onClick={() => handleFilePreview(getPassengerProofValue(passenger))}>
                                      View file
                                    </span>
                                  ) : 'NA'}
                                </td>
                                <td>{getPassengerContextValue(passenger, request, 'project')}</td>
                                <td>{getPassengerContextValue(passenger, request, 'vertical')}</td>
                                <td>{getPassengerContextValue(passenger, request, 'purpose')}</td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* COURIER TAB */}
        <div className="report-page">
          <div className='report-header'>
            <h2>Summary</h2>
            <div className='search-filter' ref={filterRef}>
              {showFilters && (
                <div className={`filter-options ${showFilters ? 'open-menu' : ''}`}>
                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.requests ? 'open-menu' : ''}`}>
                      {['All', 'My Requests'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.requests.includes(option)} 
                            onChange={() => handleFilterChange('requests', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('requests')}>Requests</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.requesterType ? 'open-menu' : ''}`}>
                      {['All', 'Assessor', 'Proctor', 'SME', 'Employee', 'Employee_Assessor','Trainer', 'Counselor', 'Intern'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.requesterType.includes(option)} 
                            onChange={() => handleFilterChange('requesterType', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('requesterType')}>Requester Type</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.supportType ? 'open-menu' : ''}`}>
                      {['All', 'Send Courier', 'Receive Courier'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.supportType.includes(option)} 
                            onChange={() => handleFilterChange('supportType', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('supportType')}>Support Type</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.status ? 'open-menu' : ''}`}>
                      {['All', 'Open', 'Closed'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.status.includes(option)} 
                            onChange={() => handleFilterChange('status', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('status')}>Open/Closed</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.requestStatus ? 'open-menu' : ''}`}>
                      {['All', 'Pending', 'Accepted', 'Denied', 'Cancelled'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.requestStatus.includes(option)} 
                            onChange={() => handleFilterChange('requestStatus', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('requestStatus')}>Request Status</button>
                  </div>

                  <div className="dropdown">
                    <div className={`dropdown-content ${dropdownVisible.bookingStatus ? 'open-menu' : ''}`}>
                      {['All', 'Success', 'Fail', 'Pending', 'Cancelled'].map(option => (
                        <label key={option}>
                          <input 
                            type="checkbox" 
                            value={option} 
                            checked={filterOptions.bookingStatus.includes(option)} 
                            onChange={() => handleFilterChange('bookingStatus', option)} 
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <button onClick={() => toggleDropdown('bookingStatus')}>Booking Status</button>
                  </div>
                </div>
              )}

              <div className="search-container">
                <div className="search-fields">
                  <label>
                    <strong>Search By:</strong>
                    <select value={searchHeader} onChange={handleSearchHeaderChange}>
                      <option value="requesterName">Requester Name</option>
                      <option value="requestId">Request ID</option>
                      <option value="requestDate">Request Date</option>
                      <option value="officeLocation">Office Location</option>
                      <option value="fromLocation">From Location</option>
                      <option value="toLocation">To Location</option>
                      <option value="sentFrom">Sent From</option>
                      <option value="receivedBy">Received By</option>
                      <option value="bookingBy">Booking By</option>
                      <option value="bookingDate">Booking Date</option>
                      <option value="amount">Amount</option>
                      <option value="podNumber">POD Number</option>
                      <option value="courierCo">Courier Company</option>
                      <option value="remarks">Remarks</option>
                      <option value="cancelReason">Cancel Reason</option>
                      <option value="refundAmount">Refund Amount</option>
                      <option value="lostAmount">Lost Amount</option>
                      <option value="bookingType">Booking Type</option>
                    </select>
                  </label>

                  {!['requestDate', 'bookingDate', 'date'].includes(searchHeader) ? (
                    <div className='search-input'>
                      <strong>Search Here:</strong>
                      <input 
                        type="text" 
                        value={searchTerm} 
                        onChange={handleSearchChange} 
                        placeholder='Search here...' 
                      />
                    </div>
                  ) : (
                    <div className='search-input'>
                      <strong>Search Here:</strong>
                      <DatePicker 
                        selected={dateRange.start} 
                        onChange={(dates) => setDateRange({ start: dates[0], end: dates[1] })} 
                        startDate={dateRange.start} 
                        endDate={dateRange.end} 
                        selectsRange 
                        className='date-picker' 
                      />
                    </div>
                  )}
                </div>
              </div>

              <button className='filter-button' onClick={handleFilterButtonClick}>
                <VscSettings />
                <span><strong>Filter</strong></span>
              </button>
              <button className='filter-button' onClick={handleClearFilters}>
                <FaDeleteLeft />
                <span><strong>Clear</strong></span>
              </button>
            </div>

            <button className='download-button' onClick={() => handleDownload('Courier')}>
              <FaDownload />
              <span>Download</span>
            </button>
          </div>

          {isPreviewLoading && <div className='loading'>Opening file...</div>}
          {previewUrl && <FilePreviewModal data={previewUrl} onClose={() => setPreviewUrl(null)} />}

          <div className="report-table">
            <div className="table-scroll-x">
              <table ref={tableRef}>
                <thead>
                  <tr>
                    <th data-f-bold='true'>S.No</th>
                    <th data-f-bold='true'>Request Date</th>
                    <th data-f-bold='true'>Request ID</th>
                    <th data-f-bold='true'>Requester Name</th>
                    <th data-f-bold='true'>Requester Type</th>
                    <th data-f-bold='true'>Request Date</th>
                    <th data-f-bold='true'>Support Needed</th>
                    <th data-f-bold='true'>Courier Type</th>
                    <th data-f-bold='true'>Office Location</th>
                    <th data-f-bold='true'>From Location</th>
                    <th data-f-bold='true'>To Location</th>
                    <th data-f-bold='true'>Sent From</th>
                    <th data-f-bold='true'>Received By</th>
                    <th data-f-bold='true'>Received Status</th>
                    <th data-f-bold='true'>Received On</th>
                    <th data-f-bold='true'>Status</th>
                    <th data-f-bold='true'>Booking Status</th>
                    <th data-f-bold='true'>POD Number</th>
                    <th data-f-bold='true'>Courier Company</th>
                    <th data-f-bold='true'>Amount (In Rs)</th>
                    <th data-f-bold='true'>Booking By</th>
                    <th data-f-bold='true'>Booking Date</th>
                    <th data-f-bold='true'>Booking Details</th>
                    <th data-f-bold='true'>Remarks</th>
                    <th data-f-bold='true'>Cancel Reason</th>
                    <th data-f-bold='true'>Cancel Proof</th>
                    <th data-f-bold='true'>Refund Amount (In Rs)</th>
                    <th data-f-bold='true'>Lost Amount (In Rs)</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr><td colSpan="28" className='loading'>Loading...</td></tr>
                  ) : (
                    filteredData && filteredData.map((request) => (
                      request.supportNeeded && request.supportNeeded.includes('Courier') && (
                        <tr key={request.id}>
                          <td>{courierSerialMap.get(request.id)}</td>
                          <td>{request.requestDate}</td>
                          <td>{request.id}</td>
                          <td>{request.requesterName}</td>
                          <td>{request.requesterType}</td>
                          <td>{request.requestDate}</td>
                          <td>{request.supportNeeded}</td>
                          <td>{request.courierType || 'NA'}</td>
                          <td>{request.officeLocation || 'NA'}</td>
                          <td>{request.fromLocation || 'NA'}</td>
                          <td>{request.toLocation || 'NA'}</td>
                          <td>{request.sentFrom || 'NA'}</td>
                          <td>{request.receivedBy || 'NA'}</td>
                          <td>{request.receivedStatus || 'NA'}</td>
                          <td>{request.receivedOn || 'NA'}</td>
                          <td>{request.status}</td>
                          <td>{request.bookingStatus || 'NA'}</td>
                          <td>{request.podNumber || 'NA'}</td>
                          <td>{request.courierCo || 'NA'}</td>
                          <td>{request.amount || 'NA'}</td>
                          <td>{request.bookingBy || 'NA'}</td>
                          <td>{request.bookingDate || 'NA'}</td>
                          <td data-hyperlink={request.bookingDetails} data-f-color={request.bookingDetails ? 'FF0000FF' : ''}>
                            {request.bookingDetails ? (
                              <span className="view-file-link" onClick={() => handleFilePreview(request.bookingDetails)}>
                                View file
                              </span>
                            ) : 'NA'}
                          </td>
                          <td>
                            {request.adminRemarks && request.adminRemarks.length > 0 
                              ? request.adminRemarks.map((rm, i) => (
                                  <div key={i}>{i+1}) {rm}</div>
                                )) 
                              : 'NA'}
                          </td>
                          <td>{request.cancelReason || 'NA'}</td>
                          <td data-hyperlink={request.cancelProof} data-f-color={request.cancelProof ? 'FF0000FF' : ''}>
                            {request.cancelProof ? (
                              <span className="view-file-link" onClick={() => handleFilePreview(request.cancelProof)}>
                                View file
                              </span>
                            ) : 'NA'}
                          </td>
                          <td>{request.refundAmount || 'NA'}</td>
                          <td>{request.lostAmount || 'NA'}</td>
                        </tr>
                      )
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Tabs>
      <Footer />
    </div>
  );
};

export default Report;
